import React, { useMemo, useRef, useState } from 'react'
import { SxProps } from '@mui/material'

import { useMetricActions } from 'redux/actions/Metrics'

import { TDatasetMarker } from 'components_new/molecules/charts/chartjs-plugins'
import { getParsedDate } from 'utils/dateParser'
import {
  TChartClick,
  TChartDataset
} from 'components_new/molecules/charts/types'
import { OptionProps } from 'components_new/molecules/OptionsMenu'
import { TKeyMetric } from 'types/GlobalKeyMetric'
import { Condition, FilterType } from 'types/GlobalKpiOption'
import {
  MetricStatus,
  TMetricQueryParams,
  TMetricSettings,
  isCustomEconomyMetric
} from 'types/GlobalMetric'
import { Context } from 'types/GlobalUserEvents'
import { TTask } from 'redux/hooks/Tasks'
import { TSubTask } from 'types/GlobalTask'
import {
  CustomPeriodFilter,
  PeriodFilter,
  WidgetType
} from 'types/GlobalWidget'

import { Filters } from 'utils/types'

import { useIsMobile } from 'utils/hooks'
import { useTranslation } from 'language/translator'
import {
  useAttributeOption,
  useMetricSegmentAttributeOptions
} from 'redux/hooks/AttributeOptions'
import { useCurrencyConverter } from 'redux/hooks/CurrencyConverter'
import { useMetricData } from 'redux/hooks/MetricData'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import ChangeText from 'components_new/molecules/ChangeText'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import BarChart from 'components_new/molecules/charts/BarChart'
import LineChart from 'components_new/molecules/charts/LineChart'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import ErrorPlaceholder from 'components_new/molecules/ErrorPlaceholder'
import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'
import SwitchItem from 'components_new/molecules/SwitchItem'
import ValueText from 'components_new/molecules/ValueText'

import DataStatus from 'components_new/organisms/DataStatus'
import MissingDefinitionDialog from 'components_new/organisms/dialogs/MissingDefinitionDialog'
import ConfigureSlicesDialog from 'components_new/organisms/dialogs/ConfigureSlicesDialog'
import MetricMarkersDialog from 'components_new/organisms/MetricMarkersDialog'
import MetricTitle, {
  MetricTitleProps
} from 'components_new/organisms/MetricTitle'
import DataDialog from 'components_new/organisms/DataDialog'
import {
  buildKeyMetricSource,
  buildMetricSource
} from 'components_new/organisms/DataDialog/builders'

import {
  getIncreaseIsPositive,
  getLabels,
  getLineColor,
  getMetricTitle,
  getValues,
  resolveUnit
} from 'utils/metric'

import SegmentMenuSubContent from '../Widget/SegmentMenu/SegmentMenuSubContent'
import Loading from './loading'
// REFERENCE-LINES (Homepal-only popover) — see ReferenceLinesMenu.tsx header
// for the full removal checklist if we decide to drop this feature.
import ReferenceLinesMenu from './ReferenceLinesMenu'

import { useCommentThreads } from 'redux/hooks/Comments'
import { formatValue, getPrefixValue } from '../Widget/Types/utils'
import {
  COMMENT_CHAR,
  buildSegmentPath,
  getCommentMarkers,
  getSubTaskMarkers,
  getTaskBands,
  processSegmentDrill
} from './utils'
import { AUTO_SIGNIFICANT_FIGURES, getAutoScale } from 'utils/autoFormat'

interface MetricViewBaseProps {
  chart: 'bar' | 'line'
  // one point per day, with a tick on the first of each month
  daily?: boolean
  clickOptions?: {
    breakdownBy?: boolean
    comment?: boolean
    explore?: boolean
  }
  focusedMarkerId?: string
  onAddComment?: (value: TChartClick) => void
  onClickComment?: (id: string) => void
  options?: OptionProps[]
  queryParams: TMetricQueryParams
  setQueryParams: (params: TMetricQueryParams) => void
  size?: MetricTitleProps['size']
  subTasks?: TSubTask[]
  sx?: SxProps
  tasks?: TTask[]
  TitleProps?: {
    hide?: boolean
    link?: boolean
  }
}

/**
 * The view is driven by exactly one source: a key metric (settings come
 * from the key metric itself) or a plain metric with explicit settings.
 */
export type MetricViewProps = MetricViewBaseProps &
  (
    | { keyMetric: TKeyMetric; id?: never; settings?: never }
    | { keyMetric?: undefined; id: string; settings: TMetricSettings }
  )

const MetricView = (props: MetricViewProps) => {
  const {
    chart,
    clickOptions,
    daily,
    focusedMarkerId,
    keyMetric,
    onAddComment,
    onClickComment,
    options,
    queryParams,
    setQueryParams,
    size,
    subTasks,
    sx,
    tasks,
    TitleProps
  } = props

  const { tryExportMetricDataXlsx, tryGetAllMetrics } = useMetricActions()
  const clickedSegmentRef = useRef<number | null>(null)
  const [showUnderlyingContent, setShowUnderlyingContent] = useState(false)
  const [underlyingSegment, setUnderlyingSegment] = useState<{
    filters: Filters
    label: string | number | null
    period: CustomPeriodFilter | null
  }>({ filters: {}, label: null, period: null })

  const isMobile = useIsMobile()
  const { lang, t, translateEnum } = useTranslation()
  // REFERENCE-LINES — only used to gate the popover behind Homepal staff.
  const { isAdmin, isEconomist, isHomepal } = useAccountSettings()

  const commentThreads = useCommentThreads(keyMetric?.id)

  const dataQueryParams: TMetricQueryParams = useMemo(
    () => ({
      ...queryParams,
      widget_type:
        chart === 'bar' ? WidgetType.BAR_CHART : WidgetType.LINE_CHART
    }),
    [queryParams, chart]
  )

  const {
    metric,
    missingSlices,
    rawData,
    settings,
    error: rawDataError
  } = useMetricData(
    props.keyMetric
      ? { keyMetric: props.keyMetric }
      : { id: props.id, settings: props.settings },
    dataQueryParams,
    undefined,
    { daily }
  )
  const currency = useCurrencyConverter()
  const resolvedUnit = resolveUnit(metric?.unit, currency)

  const segmentOptions = useMetricSegmentAttributeOptions(metric?.fact_table_id)
  const segmentOption = useAttributeOption(rawData?.segment_attribute_option_id)

  // states
  const [displayComments, setDisplayComments] = useState<boolean>(
    Boolean(keyMetric)
  )
  const [displayTasks, setDisplayTasks] = useState<boolean>(
    Boolean(tasks || subTasks)
  )

  const [missingSlicesOpen, setMissingSlicesOpen] = useState<boolean>(false)
  const [definitionOpen, setDefinitionOpen] = useState<boolean>(false)

  const [dialog, setDialog] = useState<{
    markers: TDatasetMarker[]
    open: boolean
    variant: 'markers'
  }>({
    markers: [],
    open: false,
    variant: 'markers'
  })

  // REFERENCE-LINES: builds the data for the drawHorizontalLines plugin
  // from line_1 / line_2 query params. Remove this block alongside the rest
  // of the reference-lines feature (see ReferenceLinesMenu.tsx for the full
  // removal checklist).
  const horizontalLines: {
    value: number
    label?: string
  }[] = []

  if (queryParams.line_1 !== undefined) {
    horizontalLines.push({
      label: queryParams.line_1_label,
      value: queryParams.line_1
    })
  }

  if (queryParams.line_2 !== undefined) {
    horizontalLines.push({
      label: queryParams.line_2_label,
      value: queryParams.line_2
    })
  }

  /*
   * Missing slices are checked before the error branch: no data was requested
   * for them (see useMetricData), and an error left over from an earlier
   * request would otherwise mask the actual, actionable reason.
   */
  if (missingSlices.length > 0) {
    return (
      <>
        <EmptyPlaceholder
          actions={
            <Button variant="text" onClick={() => setMissingSlicesOpen(true)}>
              {t('metrics', 'missing_slices_action')}
            </Button>
          }
          background={true}
          body={t('metrics', 'missing_slices_tooltip')}
          size="medium"
          title={
            translateEnum('metric_statuses', MetricStatus.MISSING_SLICES).title
          }
        />
        <ConfigureSlicesDialog
          helperText={t(
            'metrics',
            isAdmin
              ? 'missing_slices_dialog_helper'
              : 'missing_slices_dialog_helper_reader'
          )}
          onClose={(changed) => {
            if (changed) {
              tryGetAllMetrics()
            }

            setMissingSlicesOpen(false)
          }}
          open={missingSlicesOpen}
          slices={missingSlices}
        />
      </>
    )
  }

  /*-- missing definition --*/
  if (
    isCustomEconomyMetric(metric) &&
    metric.status === MetricStatus.MISSING_DEFINITION
  ) {
    const allowDefine = isAdmin || isEconomist

    return (
      <>
        <EmptyPlaceholder
          actions={
            allowDefine ? (
              <Button variant="text" onClick={() => setDefinitionOpen(true)}>
                {t('metrics', 'missing_definition_action')}
              </Button>
            ) : undefined
          }
          background={true}
          body={t(
            'metrics',
            allowDefine
              ? 'missing_definition_body'
              : 'missing_definition_body_reader'
          )}
          size="medium"
          title={
            translateEnum('metric_statuses', MetricStatus.MISSING_DEFINITION)
              .title
          }
        />
        {definitionOpen ? (
          <MissingDefinitionDialog
            metric={metric}
            onClose={() => setDefinitionOpen(false)}
          />
        ) : null}
      </>
    )
  }

  /*-- error --*/
  if (rawDataError) {
    return <ErrorPlaceholder code={500} />
  }

  /*-- missing data --*/
  if (metric?.status === MetricStatus.MISSING_DATA) {
    return (
      <EmptyPlaceholder
        background={true}
        body={t('metrics', 'no_data_body')}
        size="medium"
        title={t('metrics', 'no_data_title')}
      />
    )
  }

  /*-- loading --*/
  if (!metric || !rawData) {
    return <Loading />
  }

  // dynamic variables
  const title = keyMetric
    ? keyMetric.title
    : getMetricTitle(metric, settings, lang, translateEnum)

  // controlled values
  const labels = getLabels({ lang, rawData, segmentOption })
  const periods = rawData.data.map((row) =>
    row.period && typeof row.period === 'object' ? row.period : null
  )
  const increaseIsPositive = keyMetric
    ? keyMetric.increase_is_positive
    : getIncreaseIsPositive({ metric })
  const values = getValues({ rawData })
  const targetValues = rawData.target?.map((d) => d.value) ?? null

  // one unit for the headline, target and chart, e.g. GWh
  const { unitPrefix, unitLabel } = getAutoScale(
    [...values, ...(targetValues ?? [])],
    resolvedUnit,
    metric.unit_prefixes
  )
  const scale = (value: number | null) => getPrefixValue(value, unitPrefix)

  const exportToXlsx = () => {
    const rows = labels.map((label, index) => ({
      label,
      value: values[index]
    }))
    const segmentTitle = segmentOption
      ? segmentOption.name
      : t('widgets', 'segment_by_title')

    tryExportMetricDataXlsx(
      [segmentTitle, t('filters', 'value')],
      rows,
      keyMetric ? keyMetric.title : metric.title[lang]
    )
  }

  const nonNullValues = values.filter((v) => v !== null)
  const isTimeSeriesSegment = segmentOption?.is_time_series ?? false
  // daily data: one tick per month, on its first day
  const tickLabels = daily
    ? periods.map((period) =>
      period?.from_date.endsWith('-01')
        ? getParsedDate(period.from_date.slice(0, 7), lang, {
          year: 'short'
        })
        : null
    )
    : undefined
  const keyFigureValue =
    queryParams.segment_by && !isTimeSeriesSegment
      ? rawData.key_figure?.value ?? 0
      : nonNullValues[nonNullValues.length - 1]

  // handlers
  const handleCustomSegmentBy = (attributeOptionId: string) => {
    if (clickedSegmentRef.current === null || !rawData || !segmentOption) return

    const item = rawData.data[clickedSegmentRef.current]

    if (!item || item.segment === null) return

    const segmentPath = buildSegmentPath(
      item.period && typeof item.period === 'object' ? item.period : null,
      item.segment,
      segmentOption
    )

    setQueryParams(
      processSegmentDrill(attributeOptionId, segmentPath, queryParams)
    )
  }

  const handleMarkerClick = (markers: TDatasetMarker[]) => {
    // single marker
    if (markers.length === 1) {
      const marker = markers[0]

      if (marker.char === COMMENT_CHAR && onClickComment) {
        onClickComment(marker.id)
      }
    }
    // multiple markers
    else {
      setDialog({ markers: markers, open: true, variant: 'markers' })
    }
  }

  // close dialog
  const handleCloseDialog = () => {
    setDialog((prevState) => ({
      ...prevState,
      markers: [],
      open: false
    }))
  }

  // datasets
  const hasBudget = !!keyMetric?.metric.settings.variant_kpi_template_id

  const resolvedTargetValue =
    targetValues?.filter((v) => v !== null).pop() ?? null
  const hasTarget =
    keyMetric && keyMetric.target_activated && resolvedTargetValue !== null

  const targetDataset: TChartDataset | null = hasTarget
    ? {
        color: '#1a1a1d', // @TODO
        label: t('key_metrics', hasBudget ? 'budget' : 'target'),
        order: 1,
        type: chart,
        unit: unitLabel,
        style: 'dashed' as const,
        values: (targetValues ?? []).map(scale)
      }
    : null

  const showTasks = displayTasks && !queryParams.segment_by
  const subTaskMarkers = showTasks
    ? getSubTaskMarkers(subTasks ?? [], t('global', 'unnamed'))
    : []
  const taskBands = showTasks ? getTaskBands(tasks ?? []) : []
  const commentMarkers = displayComments
    ? getCommentMarkers(commentThreads, labels)
    : []

  const valueDataset: TChartDataset = {
    color: getLineColor({
      increaseIsPositive: increaseIsPositive,
      values: values
    }),
    label: keyMetric ? keyMetric.title : metric.title[lang],
    main: true,
    markers: [...subTaskMarkers, ...commentMarkers],
    order: 2,
    type: chart,
    unit: unitLabel,
    values: values.map(scale)
  }
  const datasets: TChartDataset[] = [
    valueDataset,
    ...(targetDataset ? [targetDataset] : [])
  ]

  // REFERENCE-LINES: entered in the metric's own unit
  const scaledHorizontalLines = horizontalLines.map((line) => ({
    ...line,
    value: scale(line.value) ?? line.value
  }))

  const onClickOptions = (clicked: TChartClick): OptionProps[] => [
    ...(clickOptions?.explore
      ? [
          {
            iconName: 'Explore' as const,
            onClick: () => {
              const idx = clickedSegmentRef.current
              const clickedItem =
                idx !== null && rawData ? rawData.data[idx] : null
              const filters: Filters = {}
              let label: string | number | null = null
              let period: CustomPeriodFilter | null = null

              if (queryParams.filter) {
                Object.entries(queryParams.filter).forEach(([key, opMap]) => {
                  if (opMap.eq.length > 0) {
                    filters[key] = {
                      attributeId: key,
                      condition: Condition.EQ,
                      type: FilterType.INPUT_VALUE,
                      value: opMap.eq
                    }
                  }
                })
              }

              if (queryParams.from_date && queryParams.to_date) {
                period = {
                  from: queryParams.from_date,
                  to: queryParams.to_date
                }
              }

              if (clickedItem && segmentOption) {
                label = clickedItem.segment
                const rawPeriod = clickedItem.period

                if (rawPeriod && typeof rawPeriod === 'object') {
                  period = {
                    from: rawPeriod.from_date,
                    to: rawPeriod.to_date
                  }
                } else if (clickedItem.segment) {
                  filters[segmentOption.attribute_id] = {
                    attributeId: segmentOption.attribute_id,
                    condition: Condition.EQ,
                    type: FilterType.INPUT_VALUE,
                    value: [clickedItem.segment]
                  }
                }
              }

              setUnderlyingSegment({ filters, label, period })
              setShowUnderlyingContent(true)
            },
            title: t('global', 'explore')
          }
        ]
      : []),
    ...(clickOptions?.comment && onAddComment && !queryParams.segment_by
      ? [
          {
            iconName: 'Comment' as const,
            onClick: () => onAddComment(clicked),
            title: t('global', 'add_comment')
          }
        ]
      : []),
    ...(clickOptions?.breakdownBy
      ? [
          {
            iconName: 'AutoFixHighOutlined' as const,
            infoTip: undefined,
            onClick: () => {},
            title: t('widgets', 'breakdown_by_placeholder'),
            content: (
              <SegmentMenuSubContent
                segmentOptions={segmentOptions.filter(
                  (opt) => opt.id !== queryParams.segment_by
                )}
                setCustomSegmentBy={handleCustomSegmentBy}
              />
            )
          }
        ]
      : [])
  ]

  // success
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
          ...sx
        }}
      >
        <Box
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            justifyContent: 'space-between',
            minWidth: 0,
            position: 'relative',
            width: '100%'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end'
            }}
          >
            {!TitleProps?.hide ? (
              <MetricTitle
                id={metric.id}
                keyMetric={keyMetric}
                link={TitleProps?.link}
                settings={settings}
                size={size}
                sx={{ mb: 1 }}
              />
            ) : null}

            <Text
              color="text.primary"
              gutterBottom={true}
              noWrap={true}
              variant={'h3'}
            >
              {formatValue(
                keyFigureValue,
                unitPrefix,
                1,
                undefined,
                undefined,
                AUTO_SIGNIFICANT_FIGURES
              )}{' '}
              {unitLabel}
            </Text>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                mb: 2
              }}
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                {hasTarget ? (
                  <ValueText
                    label={t('key_metrics', hasBudget ? 'budget' : 'target')}
                    unit={unitLabel}
                    value={scale(resolvedTargetValue)}
                  />
                ) : null}
                {daily ||
                queryParams.segment_by ||
                queryParams.from_date ||
                (queryParams.period_filter &&
                  queryParams.period_filter !==
                    PeriodFilter.LAST_12_MONTHS_TO_TODAY) ? null : (
                    <>
                      {/*-- 12m change --*/}
                      <ChangeText
                        denominator={values[0]}
                        increaseIsPositive={increaseIsPositive}
                        label={
                          translateEnum(
                            'period_filters',
                            PeriodFilter.LAST_12_MONTHS_TO_TODAY
                          ).short
                        }
                        numerator={values[values.length - 1]}
                        unit={resolvedUnit}
                        unitPrefixes={metric.unit_prefixes}
                      />

                      {/*-- 1m change --*/}
                      <ChangeText
                        denominator={values[values.length - 2]}
                        increaseIsPositive={increaseIsPositive}
                        label={labels.at(-1) ?? ''}
                        numerator={values[values.length - 1]}
                        unit={resolvedUnit}
                        unitPrefixes={metric.unit_prefixes}
                      />
                    </>
                  )}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              mt: -0.5
            }}
          >
            {keyMetric ? (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 0.25
                }}
              >
                <SwitchItem
                  checked={displayTasks}
                  label={t('tasks', 'tasks')}
                  onChange={() => setDisplayTasks(!displayTasks)}
                  size="small"
                  TextProps={{ color: 'text.secondary' }}
                />
                <SwitchItem
                  checked={displayComments}
                  label={t('comments', 'comments')}
                  onChange={() => setDisplayComments(!displayComments)}
                  size="small"
                  TextProps={{ color: 'text.secondary' }}
                />
              </Box>
            ) : null}

            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 0.25
              }}
            >
              <DataStatus variant="chip" />

              {/* REFERENCE-LINES — Homepal-only popover. */}
              {isHomepal ? (
                <ReferenceLinesMenu
                  queryParams={queryParams}
                  resolvedUnit={resolvedUnit}
                  setQueryParams={setQueryParams}
                />
              ) : null}

              <Tooltip title={t('global', 'export_as_xlsx')}>
                <IconButton onClick={() => exportToXlsx()} size="small">
                  <Icon fontSize="small" name="Download" />
                </IconButton>
              </Tooltip>

              {options ? (
                <OptionsMenuButton
                  color="default"
                  edge="end"
                  options={options}
                  size="small"
                />
              ) : null}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            gap: 2,
            minHeight: 0,
            minWidth: 0,
            width: '100%'
          }}
        >
          {chart === 'bar' ? (
            <BarChart
              data={{
                datasets: datasets,
                horizontalLines: scaledHorizontalLines, // REFERENCE-LINES
                labels: labels,
                isTimeseries: !queryParams.segment_by,
                periods,
                tickLabels
              }}
              flexItem={true}
              focusedMarkerId={focusedMarkerId}
              onClickComment={() => alert('Comment')}
              onMarkerClick={handleMarkerClick}
              onSegmentClick={(segment) => {
                clickedSegmentRef.current = segment
              }}
              options={onClickOptions}
              significantFigures={AUTO_SIGNIFICANT_FIGURES}
              variant="detailed"
            />
          ) : (
            <LineChart
              data={{
                datasets: datasets,
                horizontalLines: scaledHorizontalLines, // REFERENCE-LINES

                labels: labels,
                isTimeseries: !queryParams.segment_by,
                bands: taskBands,
                periods,
                tickLabels
              }}
              flexItem={true}
              focusedMarkerId={focusedMarkerId}
              onMarkerClick={handleMarkerClick}
              onSegmentClick={(segment) => {
                clickedSegmentRef.current = segment
              }}
              options={onClickOptions}
              significantFigures={AUTO_SIGNIFICANT_FIGURES}
              variant="detailed"
            />
          )}
        </Box>
      </Box>

      {/*-- multiple markers --*/}
      {keyMetric ? (
        <MetricMarkersDialog
          keyMetric={keyMetric}
          label={labels[dialog.markers[0]?.index]}
          markers={dialog.markers}
          onClose={handleCloseDialog}
          open={dialog.open && dialog.variant === 'markers'}
        />
      ) : null}

      {/*-- show content --*/}
      <DataDialog
        source={
          showUnderlyingContent
            ? keyMetric
              ? buildKeyMetricSource(keyMetric)
              : buildMetricSource({
                id: metric.id,
                settings,
                entityId: metric.entity?.id ?? null,
                title: title,
                unit: metric.unit
              })
            : null
        }
        baseFilters={queryParams.filter}
        basePeriod={
          queryParams.from_date && queryParams.to_date
            ? { from: queryParams.from_date, to: queryParams.to_date }
            : queryParams.period_filter ?? null
        }
        eventContext={{
          context: keyMetric
            ? Context.FROM_KEY_METRICS_PAGE
            : Context.FROM_METRIC_PAGE
        }}
        onClose={() => {
          setShowUnderlyingContent(false)
          setUnderlyingSegment({ filters: {}, label: null, period: null })
        }}
        segmentFilters={underlyingSegment.filters}
        segmentLabel={underlyingSegment.label}
        segmentPeriod={underlyingSegment.period}
      />
    </>
  )
}

export default MetricView
